import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import type {
  OccumStore,
  AuthUser,
  Channel,
  ChannelEvent,
  Member,
  Agent,
  WorkUpdateEntry,
} from "./types";

/** Sessions with no updates for this long are considered stale */
const STALE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export const useStore = create<OccumStore>()(
  devtools(
    immer((set, get) => ({
      // ── Initial State ───────────────────────────────────────────────────────

      auth: {
        ready: false,
        token: null,
        user: null,
        isNewLogin: false,
      },

      channels: {
        byId: {},
        orderedIds: [],
        loading: false,
      },

      messages: {
        byChannel: {},
      },

      work: {
        bySessionId: {},
      },

      members: {
        byChannel: {},
      },

      agents: {
        byId: {},
        orderedIds: [],
        loading: false,
      },

      unreads: {
        byChannel: {},
      },

      ui: {
        selectedChannelId: null,
        sidebarCollapsed: false,
        membersPanelOpen: false,
        isMobile: false,
        wsStatus: "disconnected",
        lastEventId: null,
      },

      toasts: [],

      // ── Auth Actions ──────────────────────────────────────────────────────

      setAuth(token: string, user: AuthUser, isNewLogin: boolean) {
        set((draft) => {
          draft.auth = { ready: true, token, user, isNewLogin };
        });
      },

      restoreAuth(token: string, user: AuthUser | null) {
        set((draft) => {
          draft.auth = { ready: true, token, user, isNewLogin: false };
        });
      },

      updateUser(patch: Partial<AuthUser>) {
        set((draft) => {
          if (draft.auth.user) {
            Object.assign(draft.auth.user, patch);
          }
        });
      },

      setUser(user: AuthUser) {
        set((draft) => {
          draft.auth.user = user;
        });
      },

      clearNewLogin() {
        set((draft) => {
          draft.auth.isNewLogin = false;
        });
      },

      clearAuth() {
        set((draft) => {
          draft.auth = { ready: true, token: null, user: null, isNewLogin: false };
          // Clear all data on logout
          draft.channels = { byId: {}, orderedIds: [], loading: false };
          draft.messages = { byChannel: {} };
          draft.work = { bySessionId: {} };
          draft.members = { byChannel: {} };
          draft.agents = { byId: {}, orderedIds: [], loading: false };
          draft.unreads = { byChannel: {} };
          draft.ui.selectedChannelId = null;
          draft.ui.wsStatus = "disconnected";
          draft.ui.lastEventId = null;
        });
      },

      // ── Channel Actions ───────────────────────────────────────────────────

      setChannels(channels: Channel[]) {
        set((draft) => {
          const byId: Record<string, Channel> = {};
          const orderedIds: string[] = [];
          for (const ch of channels) {
            byId[ch.id] = ch;
            orderedIds.push(ch.id);
          }
          draft.channels.byId = byId;
          draft.channels.orderedIds = orderedIds;
          draft.channels.loading = false;
        });
      },

      setChannelsLoading(loading: boolean) {
        set((draft) => {
          draft.channels.loading = loading;
        });
      },

      // ── Message Actions ───────────────────────────────────────────────────

      setMessages(channelId: string, events: ChannelEvent[], hasMore: boolean) {
        set((draft) => {
          const eventsById: Record<string, ChannelEvent> = {};
          const eventIds: string[] = [];
          for (const e of events) {
            eventsById[e.id] = e;
            eventIds.push(e.id);
          }
          const prev = draft.messages.byChannel[channelId];
          draft.messages.byChannel[channelId] = {
            eventsById,
            eventIds,
            loading: false,
            fetchId: (prev?.fetchId ?? 0) + 1,
            hasMore,
            oldestCursor: events.length > 0 ? events[0].id : null,
          };

          // Auto-initialize work sessions from fetched work events
          for (const evt of events) {
            if (evt.eventType !== "work") continue;
            const sid = (evt.body as any)?.sessionId;
            if (!sid || draft.work.bySessionId[sid]) continue;
            draft.work.bySessionId[sid] = {
              sessionId: sid,
              channelId: evt.channelId,
              agentId: evt.senderAgentId,
              eventId: evt.id,
              startedAt: (evt.body as any).startedAt ?? evt.createdAt,
              completedAt: (evt.body as any).completedAt ?? null,
              summary: (evt.body as any).summary ?? null,
              updates: [],
              updatesLoaded: false,
            };
          }

        });
      },

      prependMessages(channelId: string, events: ChannelEvent[], hasMore: boolean) {
        set((draft) => {
          const bucket = draft.messages.byChannel[channelId];
          if (!bucket) return;
          // Collect new IDs to prepend in order
          const newIds: string[] = [];
          for (const e of events) {
            if (!bucket.eventsById[e.id]) {
              bucket.eventsById[e.id] = e;
              newIds.push(e.id);
            }
          }
          if (newIds.length > 0) {
            bucket.eventIds = [...newIds, ...bucket.eventIds];
          }
          bucket.hasMore = hasMore;
          bucket.oldestCursor = events.length > 0 ? events[0].id : bucket.oldestCursor;
          bucket.loading = false;

          // Auto-initialize work sessions from older events
          for (const evt of events) {
            if (evt.eventType !== "work") continue;
            const sid = (evt.body as any)?.sessionId;
            if (!sid || draft.work.bySessionId[sid]) continue;
            draft.work.bySessionId[sid] = {
              sessionId: sid,
              channelId: evt.channelId,
              agentId: evt.senderAgentId,
              eventId: evt.id,
              startedAt: (evt.body as any).startedAt ?? evt.createdAt,
              completedAt: (evt.body as any).completedAt ?? null,
              summary: (evt.body as any).summary ?? null,
              updates: [],
              updatesLoaded: false,
            };
          }
        });
      },

      appendMessage(event: ChannelEvent) {
        set((draft) => {
          const bucket = draft.messages.byChannel[event.channelId];
          if (!bucket) return;
          // Deduplicate
          if (bucket.eventsById[event.id]) return;
          bucket.eventsById[event.id] = event;
          bucket.eventIds.push(event.id);
        });
      },

      setMessagesLoading(channelId: string, loading: boolean) {
        set((draft) => {
          const bucket = draft.messages.byChannel[channelId];
          if (bucket) {
            bucket.loading = loading;
          } else {
            draft.messages.byChannel[channelId] = {
              eventsById: {},
              eventIds: [],
              loading,
              fetchId: 0,
              hasMore: true,
              oldestCursor: null,
            };
          }
        });
      },

      // ── Work Actions ──────────────────────────────────────────────────────

      initWorkFromEvents(events: ChannelEvent[], channelId: string | null) {
        set((draft) => {
          for (const evt of events) {
            if (evt.eventType !== "work") continue;
            if (channelId && evt.channelId !== channelId) continue;
            const sid = (evt.body as any)?.sessionId;
            if (!sid) continue;
            // Don't overwrite an active session that may have live updates
            if (draft.work.bySessionId[sid]) continue;
            draft.work.bySessionId[sid] = {
              sessionId: sid,
              channelId: evt.channelId,
              agentId: evt.senderAgentId,
              eventId: evt.id,
              startedAt: (evt.body as any).startedAt ?? evt.createdAt,
              completedAt: (evt.body as any).completedAt ?? null,
              summary: (evt.body as any).summary ?? null,
              updates: [],
              updatesLoaded: false,
            };
          }
        });
      },

      onWorkUpdate(data) {
        set((draft) => {
          const session = draft.work.bySessionId[data.sessionId];
          if (!session) return;
          // Deduplicate
          if (session.updates.some((u) => u.id === data.id)) return;
          session.updates.push({
            id: data.id,
            updateType: data.updateType,
            content: data.content,
            toolName: data.toolName ?? null,
            createdAt: data.createdAt,
          });
        });
      },

      onWorkComplete(data) {
        set((draft) => {
          const session = draft.work.bySessionId[data.sessionId];
          if (!session) return;
          session.completedAt = data.completedAt;
          session.summary = data.summary;
        });
      },

      setWorkUpdates(sessionId: string, updates: WorkUpdateEntry[]) {
        set((draft) => {
          const session = draft.work.bySessionId[sessionId];
          if (!session) return;
          session.updates = updates;
          session.updatesLoaded = true;
        });
      },

      markWorkStale(sessionId: string) {
        set((draft) => {
          const session = draft.work.bySessionId[sessionId];
          if (session) {
            session.stale = true;
          }
        });
      },

      resetWork() {
        set((draft) => {
          draft.work.bySessionId = {};
        });
      },

      // ── Member Actions ────────────────────────────────────────────────────

      setMembers(channelId: string, members: Member[]) {
        const users = new Map<string, string>();
        const agents = new Map<string, string>();
        for (const m of members) {
          const name = m.displayName ?? m.invitedName;
          if (m.memberType === "user" && m.userId && name) users.set(m.userId, name);
          if (m.memberType === "agent" && m.agentId && name) agents.set(m.agentId, name);
        }
        set((draft) => {
          draft.members.byChannel[channelId] = {
            list: members,
            loading: false,
            userDisplayNames: users,
            agentDisplayNames: agents,
          };
        });
      },

      setMembersLoading(channelId: string, loading: boolean) {
        set((draft) => {
          const bucket = draft.members.byChannel[channelId];
          if (bucket) {
            bucket.loading = loading;
          } else {
            draft.members.byChannel[channelId] = {
              list: [],
              loading,
              userDisplayNames: new Map(),
              agentDisplayNames: new Map(),
            };
          }
        });
      },

      // ── Agent Actions ─────────────────────────────────────────────────────

      setAgents(agents: Agent[]) {
        set((draft) => {
          const byId: Record<string, Agent> = {};
          const orderedIds: string[] = [];
          for (const a of agents) {
            byId[a.id] = a;
            orderedIds.push(a.id);
          }
          draft.agents.byId = byId;
          draft.agents.orderedIds = orderedIds;
          draft.agents.loading = false;
        });
      },

      setAgentsLoading(loading: boolean) {
        set((draft) => {
          draft.agents.loading = loading;
        });
      },

      // ── Unread Actions ────────────────────────────────────────────────────

      initUnreadsFromChannels(channels: Channel[]) {
        set((draft) => {
          for (const ch of channels) {
            const existing = draft.unreads.byChannel[ch.id];
            draft.unreads.byChannel[ch.id] = {
              lastEventId: existing?.lastEventId ?? ch.lastEventId ?? null,
              lastReadEventId: existing?.lastReadEventId ?? ch.lastReadEventId ?? null,
              lastEventType: existing?.lastEventType ?? ch.lastEventType ?? null,
              workActive: existing?.workActive ?? false,
            };
          }
        });
      },

      onUnreadEvent(event) {
        set((draft) => {
          const existing = draft.unreads.byChannel[event.channelId];
          draft.unreads.byChannel[event.channelId] = {
            lastEventId: event.id,
            lastReadEventId: existing?.lastReadEventId ?? null,
            lastEventType: event.eventType,
            workActive: event.eventType === "work" ? true : (existing?.workActive ?? false),
          };
        });
      },

      onUnreadWorkComplete(channelId: string) {
        set((draft) => {
          const existing = draft.unreads.byChannel[channelId];
          if (existing) {
            existing.workActive = false;
          }
        });
      },

      onReadCursorUpdate(channelId: string, userId: string, eventId: string, currentUserId: string | undefined) {
        if (userId !== currentUserId) return;
        set((draft) => {
          const existing = draft.unreads.byChannel[channelId];
          if (existing) {
            existing.lastReadEventId = eventId;
          }
        });
      },

      markChannelRead(channelId: string, eventId: string) {
        set((draft) => {
          const existing = draft.unreads.byChannel[channelId];
          if (existing) {
            existing.lastReadEventId = eventId;
            existing.workActive = false;
          }
        });
      },

      // ── UI Actions ────────────────────────────────────────────────────────

      selectChannel(channelId: string | null) {
        set((draft) => {
          draft.ui.selectedChannelId = channelId;
          draft.ui.membersPanelOpen = false;
        });
      },

      toggleSidebar() {
        set((draft) => {
          draft.ui.sidebarCollapsed = !draft.ui.sidebarCollapsed;
        });
      },

      setSidebarCollapsed(collapsed: boolean) {
        set((draft) => {
          draft.ui.sidebarCollapsed = collapsed;
        });
      },

      toggleMembersPanel() {
        set((draft) => {
          draft.ui.membersPanelOpen = !draft.ui.membersPanelOpen;
        });
      },

      setMembersPanelOpen(open: boolean) {
        set((draft) => {
          draft.ui.membersPanelOpen = open;
        });
      },

      setIsMobile(mobile: boolean) {
        set((draft) => {
          draft.ui.isMobile = mobile;
          if (mobile) draft.ui.sidebarCollapsed = true;
        });
      },

      setWsStatus(status) {
        set((draft) => {
          draft.ui.wsStatus = status;
        });
      },

      setLastEventId(id: string) {
        set((draft) => {
          // Only update if this ID is greater (ULIDs are lexicographically ordered)
          if (!draft.ui.lastEventId || id > draft.ui.lastEventId) {
            draft.ui.lastEventId = id;
          }
        });
      },

      // ── Toast Actions ──────────────────────────────────────────────────────

      addToast(message: string, type: "error" | "success" | "info" = "error") {
        set((draft) => {
          draft.toasts.push({
            id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
            message,
            type,
          });
        });
      },

      removeToast(id: string) {
        set((draft) => {
          draft.toasts = draft.toasts.filter((t) => t.id !== id);
        });
      },
    })),
    { name: "occum-store" }
  )
);

// ── Toast emitter bridge ──────────────────────────────────────────────────
// Subscribes to the plain-JS toast emitter so that api.ts (which can't import
// React or Zustand) can trigger toasts via emitToast().
import { onToast } from "../lib/toastEmitter";
onToast((message, type) => {
  useStore.getState().addToast(message, type);
});

// ── Stale session detection ───────────────────────────────────────────────
// Runs outside React — checks every 30s for work sessions with no activity
let staleInterval: ReturnType<typeof setInterval> | null = null;

export function startStaleDetection() {
  if (staleInterval) return;
  staleInterval = setInterval(() => {
    const state = useStore.getState();
    const now = Date.now();
    for (const [sid, session] of Object.entries(state.work.bySessionId)) {
      if (session.completedAt || session.stale) continue;
      const lastActivity = session.updates.length > 0
        ? new Date(session.updates[session.updates.length - 1].createdAt).getTime()
        : new Date(session.startedAt).getTime();
      if (now - lastActivity > STALE_TIMEOUT_MS) {
        state.markWorkStale(sid);
      }
    }
  }, 30_000);
}

export function stopStaleDetection() {
  if (staleInterval) {
    clearInterval(staleInterval);
    staleInterval = null;
  }
}
